import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty
} from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import {
	createApp,
	IntegrationAppSaveModel
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type CreateAppDialogProps = {
	onClose: () => void;
	onSave: (appId: number) => void;
};

export const CreateAppDialog = (props: CreateAppDialogProps): ReactElement => {
	const {
		onClose,
		onSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<IntegrationAppSaveModel>(EMPTY_FORM_VALUE);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<IntegrationAppSaveModel> = useFormHandler<IntegrationAppSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				return;
			}

			setIsBusy(true);

			try {
				const appId: number = await createApp(
					model,
					globalCancellation
				);

				onSave(appId);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isBusy,
			model,
			onSave
		]
	);

	return (
		<Dialog
			trackingId="create-integration-app-dialog"
		>
			<Dialog.Header
				title={localize("_CreateNewApp")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					<FormItem label={localize("_Name")} isRequired>
						<TextInput
							{...formHandler.for("name")
								.attachToTextInput()
							}
							placeholder={localize("_EnterAppName")}
						/>
						<ValidationError
							{...formHandler.for("name").attachToValidationError()}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Create")}
						isDisabled={isBusy}
						onClick={saveHandler}
						trackingId="create"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
CreateAppDialog.displayName = "CreateAppDialog";

export type CreateAppDialogInitialData = Omit<
	CreateAppDialogProps,
	"onClose" | "onSave"
>;

export function useCreateAppDialog(): [
	element: ReactElement | null,
	open: (props: CreateAppDialogInitialData) => Promise<TryResult<number>>
] {
	return useDataInputDialog<CreateAppDialogInitialData, number>(
		(props, onClose, onSave) => (
			<CreateAppDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

const EMPTY_FORM_VALUE: IntegrationAppSaveModel = {
	name: ""
};

const validationConfiguration: IValidationConfiguration<IntegrationAppSaveModel> =
	createValidationConfiguration<IntegrationAppSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);
